import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useState, useEffect } from "react";
import LoadingButton from "../../../components/LoadingButton";
import TagsAutocomplete from "../../../components/TagsAutocomplete";
import { Document } from "../../../types";

interface CreateDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  onCreateDocument: (document: Partial<Document>) => Promise<void>;
  availableTags: string[];
  loading?: boolean;
  companyId: string;
  getToken: () => Promise<string>;
}

export default function CreateDocumentDialog({
  open,
  onClose,
  onCreateDocument,
  availableTags,
  loading = false,
  companyId,
  getToken,
}: CreateDocumentDialogProps) {
  const [name, setName] = useState("");
  const [content, setContent] = useState("");
  const [tags, setTags] = useState<string[]>([]);

  const handleClose = () => {
    setName("");
    setContent("");
    setTags([]);
    onClose();
  };

  const handleCreate = async () => {
    if (name.trim() && content.trim()) {
      await onCreateDocument({
        name: name.trim(),
        content: content.trim(),
        tags,
        type: "txt",
      });
      handleClose();
    }
  };

  const isValid = name.trim() && content.trim();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Create Document
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 3 }}>
          Create a new text document in your knowledge base.
        </Typography>
        
        <TextField
          fullWidth
          label="Document Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mb: 2 }}
          autoFocus
          disabled={loading}
          required
        />

        <TagsAutocomplete
          value={tags}
          onChange={setTags}
          companyId={companyId}
          getToken={getToken}
          placeholder="Add tags to categorize this document"
          disabled={loading}
          style={{ marginBottom: 16 }}
        />

        <TextField
          fullWidth
          multiline
          rows={15}
          label="Content"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          disabled={loading}
          required
          placeholder="Enter the document content..."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleCreate}
          loading={loading}
          disabled={!isValid}
        >
          Create Document
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}